import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CopyIcon, PencilIcon } from 'lucide-react';
import {
  BackLink,
  Button,
  DatePicker,
  ErrorNotice,
  FactList,
  Field,
  LoadingState,
  PageHeading,
  Panel,
  Pill,
  SelectInput,
  TextArea,
  TextInput,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import {
  INDOOR_OUTDOOR,
  SHIPPING_MODE,
  SHIPPING_MODE_LABELS,
  readEvent,
  readEventShipping,
  saveEventShipping,
  type IndoorOutdoor,
  type ShipToAddress,
  type ShippingMode,
  type ShippingPatch,
  type ShippingView,
} from '@/lib/events';

/** Where the edit screen lives; the panel links here and the page returns to the event. */
export function eventShippingPath(eventId: string): string {
  return `/events/${eventId}/shipping`;
}

/** A yes / no / not answered choice, as the venue answers it. */
const TRI = { yes: 'yes', no: 'no', unknown: 'unknown' } as const;
type Tri = (typeof TRI)[keyof typeof TRI];

function toTri(value: boolean | undefined | null): Tri {
  if (value === true) return TRI.yes;
  if (value === false) return TRI.no;
  return TRI.unknown;
}
function fromTri(value: Tri): boolean | null {
  if (value === TRI.yes) return true;
  if (value === TRI.no) return false;
  return null;
}
function triLabel(value: boolean | undefined): string | undefined {
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  return undefined;
}

/** A timestamp (confirmed-at) in the viewer's zone. */
function shortDate(iso: string | undefined): string | undefined {
  if (!iso) return undefined;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? undefined : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * A calendar day (delivery window, pickup date). Stored as UTC midnight, so
 * it must be read back as UTC or the day before shows west of Greenwich.
 */
function shortDay(iso: string | undefined): string | undefined {
  if (!iso) return undefined;
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function dateInput(iso: string | undefined): string {
  return (iso ?? '').slice(0, 10);
}

/** The small uppercase label the overview panels use for sub-sections. */
function SubLabel({ children }: { readonly children: React.ReactNode }): React.ReactElement {
  return <p className="text-portal-micro font-extrabold tracking-[0.6px] text-portal-muted uppercase">{children}</p>;
}

/**
 * The venue receiving profile on the event overview: the label as the
 * warehouse prints it, whether the venue confirmed, and the dock facts.
 * Every field of the profile shows, "Not set" where empty, so what is still
 * unknown about the venue is as visible as what is known. Editing happens on
 * its own screen; the profile is too long for a dialog.
 *
 * @param props - The event id and whether the viewer may edit.
 * @returns The panel.
 */
export function ShippingPanel({
  eventId,
  canEdit,
}: {
  readonly eventId: string;
  readonly canEdit: boolean;
}): React.ReactElement {
  const [copied, setCopied] = useState(false);
  const shipping = useQuery({
    queryKey: ['event-shipping', eventId],
    queryFn: ({ signal }) => readEventShipping(eventId, signal),
  });

  if (shipping.isPending) return <LoadingState label="Loading shipping…" minimal />;
  if (shipping.isError) {
    return <ErrorNotice inline title="Shipping unavailable" message={errorMessage(shipping.error)} />;
  }

  const view = shipping.data;
  const p = view.profile;
  const editButton = canEdit ? (
    <Link
      to={eventShippingPath(eventId)}
      className="inline-flex items-center gap-1.5 rounded-portal-input border border-portal-line bg-white px-3 py-1.5 text-portal-small font-semibold text-portal-ink hover:bg-admin-subtle"
    >
      <PencilIcon aria-hidden className="size-3.5" />
      Edit
    </Link>
  ) : null;

  function copyLabel(): void {
    navigator.clipboard
      .writeText(view.labelLines.join('\n'))
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {});
  }

  return (
    <Panel className="p-5">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-portal-micro font-extrabold tracking-[1px] text-portal-muted uppercase">Shipping</h2>
        <div className="flex items-center gap-2">
          {p.mode === SHIPPING_MODE.none ? (
            <Pill tone="neutral">No shipping</Pill>
          ) : view.confirmed ? (
            <Pill tone="success">
              Confirmed{p.confirmed?.with ? ` with ${p.confirmed.with}` : ''}
              {shortDate(p.confirmed?.at) ? ` · ${shortDate(p.confirmed?.at)}` : ''}
            </Pill>
          ) : (
            <Pill tone="warning">Unconfirmed</Pill>
          )}
          {editButton}
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,320px)_1fr]">
        <div>
          <SubLabel>Ship to</SubLabel>
          {view.labelLines.length > 0 ? (
            <>
              <pre className="mt-1 whitespace-pre-line rounded-md bg-admin-subtle px-3 py-2 font-sans text-portal-small text-portal-ink">
                {view.labelLines.join('\n')}
              </pre>
              <Button variant="ghost" size="compact" className="mt-1" onClick={copyLabel}>
                <CopyIcon aria-hidden className="size-3.5" />
                {copied ? 'Copied' : 'Copy label'}
              </Button>
            </>
          ) : (
            <p className="mt-1 text-portal-small text-portal-muted/70 italic">
              {p.mode === SHIPPING_MODE.none ? 'Nothing ships for this event.' : 'Not set'}
            </p>
          )}
        </div>
        <FactList
          columns={3}
          showEmpty
          facts={[
            { label: 'Destination', value: p.mode ? SHIPPING_MODE_LABELS[p.mode] : undefined },
            { label: 'Hold for', value: p.holdFor },
            { label: 'Attn', value: p.shipTo?.attn },
            {
              label: 'Delivery window',
              value:
                shortDay(p.deliveryWindow?.start) || shortDay(p.deliveryWindow?.end)
                  ? [shortDay(p.deliveryWindow?.start), shortDay(p.deliveryWindow?.end)].filter(Boolean).join(' – ')
                  : undefined,
            },
            { label: 'Receiving hours', value: p.receivingHours },
            {
              label: 'Indoor / outdoor',
              value:
                p.indoorOutdoor && p.indoorOutdoor !== INDOOR_OUTDOOR.unknown
                  ? p.indoorOutdoor === INDOOR_OUTDOOR.indoor
                    ? 'Indoor'
                    : 'Outdoor'
                  : undefined,
            },
            { label: 'Pallets OK', value: triLabel(p.palletsOk) },
            { label: 'Lift gate', value: triLabel(p.liftGate) },
            { label: 'Inside delivery', value: triLabel(p.insideDelivery) },
            { label: 'Return instructions', value: p.returnInstructions },
            {
              label: 'Return pickup',
              value:
                p.returnPickup?.carrier || p.returnPickup?.date
                  ? [p.returnPickup?.carrier, shortDay(p.returnPickup?.date)].filter(Boolean).join(' · ')
                  : undefined,
            },
            {
              label: 'Confirmed with',
              value: view.confirmed
                ? `${p.confirmed?.with ?? ''}${shortDate(p.confirmed?.at) ? ` · ${shortDate(p.confirmed?.at)}` : ''}`
                : undefined,
            },
            { label: 'Notes', value: p.notes },
          ]}
        />
      </div>
    </Panel>
  );
}

/**
 * The shipping and receiving screen for one event: every field of the
 * profile in sections, saved with one PUT, then back to the event.
 *
 * @returns The page.
 */
export function EventShippingPage(): React.ReactElement {
  const { eventId = '' } = useParams<{ eventId: string }>();
  const event = useQuery({
    queryKey: ['event', eventId],
    queryFn: ({ signal }) => readEvent(eventId, signal),
    enabled: eventId.length > 0,
  });
  const shipping = useQuery({
    queryKey: ['event-shipping', eventId],
    queryFn: ({ signal }) => readEventShipping(eventId, signal),
    enabled: eventId.length > 0,
  });

  const title = event.data
    ? [event.data.contractNumber, event.data.accountName].filter(Boolean).join(' · ')
    : 'Event';

  return (
    <section className="animate-portal-rise">
      <BackLink to={`/events/${eventId}`}>{event.data ? title : 'Back to the event'}</BackLink>
      <div className="mt-3">
        <PageHeading
          eyebrow="OPERATIONS"
          title="Shipping and receiving"
          subtitle="Where materials ship, what the dock needs to know, and who at the venue confirmed it."
          size="compact"
        />
      </div>
      {shipping.isPending ? (
        <LoadingState label="Loading shipping…" />
      ) : shipping.isError ? (
        <ErrorNotice title="Shipping unavailable" message={errorMessage(shipping.error)} />
      ) : (
        <ShippingForm eventId={eventId} view={shipping.data} />
      )}
    </section>
  );
}

/** One form for the whole profile; saves with a single PUT and returns to the event. */
function ShippingForm({ eventId, view }: { readonly eventId: string; readonly view: ShippingView }): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const p = view.profile;
  const initialTo: ShipToAddress = p.shipTo ?? view.defaultShipTo ?? {};
  const [mode, setMode] = useState<ShippingMode>(p.mode ?? SHIPPING_MODE.venue);
  const [to, setTo] = useState<ShipToAddress>(initialTo);
  const [holdFor, setHoldFor] = useState(p.holdFor ?? '');
  const [windowStart, setWindowStart] = useState(dateInput(p.deliveryWindow?.start));
  const [windowEnd, setWindowEnd] = useState(dateInput(p.deliveryWindow?.end));
  const [palletsOk, setPalletsOk] = useState<Tri>(toTri(p.palletsOk));
  const [liftGate, setLiftGate] = useState<Tri>(toTri(p.liftGate));
  const [insideDelivery, setInsideDelivery] = useState<Tri>(toTri(p.insideDelivery));
  const [receivingHours, setReceivingHours] = useState(p.receivingHours ?? '');
  const [returnInstructions, setReturnInstructions] = useState(p.returnInstructions ?? '');
  const [returnCarrier, setReturnCarrier] = useState(p.returnPickup?.carrier ?? '');
  const [returnDate, setReturnDate] = useState(dateInput(p.returnPickup?.date));
  const [indoorOutdoor, setIndoorOutdoor] = useState<IndoorOutdoor>(p.indoorOutdoor ?? INDOOR_OUTDOOR.unknown);
  const [confirmedWith, setConfirmedWith] = useState(p.confirmed?.with ?? '');
  const [notes, setNotes] = useState(p.notes ?? '');

  const setToField = (key: keyof ShipToAddress, value: string): void => setTo((prev) => ({ ...prev, [key]: value }));

  const save = useMutation({
    mutationFn: () => {
      const body: ShippingPatch = {
        mode,
        shipTo: mode === SHIPPING_MODE.none ? null : to,
        holdFor: holdFor.trim(),
        deliveryWindow: windowStart || windowEnd ? { start: windowStart || null, end: windowEnd || null } : null,
        palletsOk: fromTri(palletsOk),
        liftGate: fromTri(liftGate),
        insideDelivery: fromTri(insideDelivery),
        receivingHours: receivingHours.trim(),
        returnInstructions: returnInstructions.trim(),
        returnPickup: returnCarrier || returnDate ? { carrier: returnCarrier.trim(), date: returnDate || null } : null,
        indoorOutdoor,
        confirmed: confirmedWith.trim() ? { with: confirmedWith.trim() } : null,
        notes: notes.trim(),
      };
      return saveEventShipping(eventId, body);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['event-shipping', eventId] });
      navigate(`/events/${eventId}`);
    },
  });

  const triOptions = (
    <>
      <option value={TRI.unknown}>Not asked yet</option>
      <option value={TRI.yes}>Yes</option>
      <option value={TRI.no}>No</option>
    </>
  );
  const shipping = mode !== SHIPPING_MODE.none;

  return (
    <form
      className="mt-6 space-y-5"
      onSubmit={(event) => {
        event.preventDefault();
        if (!save.isPending) save.mutate();
      }}
    >
      <Panel className="p-5">
        <SubLabel>Where materials go</SubLabel>
        <div className="mt-3 max-w-md">
          <Field label="Destination" htmlFor="ship-mode">
            <SelectInput id="ship-mode" value={mode} onChange={(e) => setMode(e.target.value as ShippingMode)}>
              {(Object.keys(SHIPPING_MODE_LABELS) as ShippingMode[]).map((key) => (
                <option key={key} value={key}>
                  {SHIPPING_MODE_LABELS[key]}
                </option>
              ))}
            </SelectInput>
          </Field>
        </div>
      </Panel>

      {shipping ? (
        <div className="grid gap-5 lg:grid-cols-2">
          <Panel className="p-5">
            <div className="flex items-center justify-between">
              <SubLabel>Ship to</SubLabel>
              {view.defaultShipTo ? (
                <Button type="button" variant="ghost" size="compact" onClick={() => setTo(view.defaultShipTo ?? {})}>
                  Use the venue address
                </Button>
              ) : null}
            </div>
            <div className="mt-3 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Name" htmlFor="ship-name">
                  <TextInput id="ship-name" value={to.name ?? ''} onChange={(e) => setToField('name', e.target.value)} />
                </Field>
                <Field label="Attn" htmlFor="ship-attn">
                  <TextInput id="ship-attn" value={to.attn ?? ''} placeholder="Who receives it" onChange={(e) => setToField('attn', e.target.value)} />
                </Field>
              </div>
              <Field label="Street" htmlFor="ship-street">
                <TextInput id="ship-street" value={to.street ?? ''} onChange={(e) => setToField('street', e.target.value)} />
              </Field>
              <div className="grid gap-4 sm:grid-cols-[1fr_90px_110px]">
                <Field label="City" htmlFor="ship-city">
                  <TextInput id="ship-city" value={to.city ?? ''} onChange={(e) => setToField('city', e.target.value)} />
                </Field>
                <Field label="State" htmlFor="ship-state">
                  <TextInput id="ship-state" value={to.state ?? ''} onChange={(e) => setToField('state', e.target.value)} />
                </Field>
                <Field label="ZIP" htmlFor="ship-zip">
                  <TextInput id="ship-zip" value={to.postalCode ?? ''} onChange={(e) => setToField('postalCode', e.target.value)} />
                </Field>
              </div>
              <Field label="Phone" htmlFor="ship-phone">
                <TextInput id="ship-phone" value={to.phone ?? ''} onChange={(e) => setToField('phone', e.target.value)} />
              </Field>
              <Field label="Hold for" htmlFor="ship-hold">
                <TextInput id="ship-hold" value={holdFor} placeholder="Mary Bohan / Tito's NSM - 9.14.26" onChange={(e) => setHoldFor(e.target.value)} />
              </Field>
            </div>
          </Panel>

          <Panel className="p-5">
            <SubLabel>Delivery</SubLabel>
            <div className="mt-3 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Window from" htmlFor="ship-win-start">
                  <DatePicker id="ship-win-start" value={windowStart} placeholder="Earliest" onChange={setWindowStart} />
                </Field>
                <Field label="Window to" htmlFor="ship-win-end">
                  <DatePicker id="ship-win-end" value={windowEnd} placeholder="Latest" onChange={setWindowEnd} />
                </Field>
              </div>
              <Field label="Receiving hours" htmlFor="ship-hours">
                <TextInput id="ship-hours" value={receivingHours} placeholder="Mon–Fri 8am–4pm" onChange={(e) => setReceivingHours(e.target.value)} />
              </Field>
              <div className="grid gap-4 sm:grid-cols-3">
                <Field label="Pallets OK" htmlFor="ship-pallets">
                  <SelectInput id="ship-pallets" value={palletsOk} onChange={(e) => setPalletsOk(e.target.value as Tri)}>{triOptions}</SelectInput>
                </Field>
                <Field label="Lift gate" htmlFor="ship-liftgate">
                  <SelectInput id="ship-liftgate" value={liftGate} onChange={(e) => setLiftGate(e.target.value as Tri)}>{triOptions}</SelectInput>
                </Field>
                <Field label="Inside delivery" htmlFor="ship-inside">
                  <SelectInput id="ship-inside" value={insideDelivery} onChange={(e) => setInsideDelivery(e.target.value as Tri)}>{triOptions}</SelectInput>
                </Field>
              </div>
              <Field label="Indoor or outdoor" htmlFor="ship-inout">
                <SelectInput id="ship-inout" value={indoorOutdoor} onChange={(e) => setIndoorOutdoor(e.target.value as IndoorOutdoor)}>
                  <option value={INDOOR_OUTDOOR.unknown}>Not known yet</option>
                  <option value={INDOOR_OUTDOOR.indoor}>Indoor</option>
                  <option value={INDOOR_OUTDOOR.outdoor}>Outdoor</option>
                </SelectInput>
              </Field>
            </div>
          </Panel>

          <Panel className="p-5">
            <SubLabel>Returns</SubLabel>
            <div className="mt-3 space-y-4">
              <Field label="Return instructions" htmlFor="ship-return">
                <TextArea id="ship-return" rows={3} value={returnInstructions} placeholder="Leave return boxes at the hotel; ensure they are labeled" onChange={(e) => setReturnInstructions(e.target.value)} />
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Pickup carrier" htmlFor="ship-return-carrier">
                  <TextInput id="ship-return-carrier" value={returnCarrier} placeholder="FedEx" onChange={(e) => setReturnCarrier(e.target.value)} />
                </Field>
                <Field label="Pickup date" htmlFor="ship-return-date">
                  <DatePicker id="ship-return-date" value={returnDate} placeholder="When to schedule" onChange={setReturnDate} />
                </Field>
              </div>
            </div>
          </Panel>

          <Panel className="p-5">
            <SubLabel>Confirmation</SubLabel>
            <div className="mt-3 space-y-4">
              <Field label="Confirmed with (name at the venue)" htmlFor="ship-confirmed">
                <TextInput id="ship-confirmed" value={confirmedWith} placeholder="Leave blank until the venue confirms" onChange={(e) => setConfirmedWith(e.target.value)} />
              </Field>
              {view.confirmed && p.confirmed?.with === confirmedWith.trim() ? (
                <p className="text-portal-micro text-portal-muted">
                  Confirmed {shortDate(p.confirmed?.at) ?? ''}. Change the name to record a new confirmation.
                </p>
              ) : null}
              <Field label="Notes" htmlFor="ship-notes">
                <TextArea id="ship-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
              </Field>
            </div>
          </Panel>
        </div>
      ) : (
        <Panel className="p-5">
          <Field label="Notes" htmlFor="ship-notes">
            <TextArea id="ship-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>
        </Panel>
      )}

      {save.isError ? <ErrorNotice inline title="Could not save" message={errorMessage(save.error)} /> : null}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="secondary" onClick={() => navigate(`/events/${eventId}`)} disabled={save.isPending}>
          Cancel
        </Button>
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </form>
  );
}
